import {
  configureStore,
  ThunkAction,
  Action,
  EntityState,
  EntityId,
  getDefaultMiddleware,
} from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import {
  projectsReducer,
  authorProfileReducer,
  fetchProjectsData,
  messagesReducer,
  notificationsReducer,
  adminReducer,
} from "./features";
import createSagaMiddleware from "redux-saga";
import { checkAdminSession, rootSaga } from "./sagas";

//
const sagaMiddleware = createSagaMiddleware();

export const store = configureStore({
  reducer: {
    projects: projectsReducer,
    authorProfile: authorProfileReducer,
    messages: messagesReducer,
    notifications: notificationsReducer,
    admin: adminReducer,
  },
  devTools: process.env.NODE_ENV === "production" ? false : true,
  middleware: getDefaultMiddleware().concat(sagaMiddleware),
});
export default store;

// redux saga
sagaMiddleware.run(rootSaga);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

// INITIALIZE THE STORE FROM THE DATABASE
store.dispatch(fetchProjectsData());
// store.dispatch(fetchAuthorData());
store.dispatch(checkAdminSession());

// Hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

type WithId = { id: EntityId };
export function defaultSelector<T extends WithId>(e: T): EntityId {
  return e.id;
}

function hasId(e: unknown): e is WithId {
  return (
    typeof (e as WithId).id === "string" || typeof (e as WithId).id === "number"
  );
}

// the user can supply a custom selector or use a default selector that just selects the id property
export function generateEntityStateFromArray<
  Entity,
  Selector extends (x: Entity) => EntityId
>(arr: Entity[], selector?: Selector): EntityState<Entity> {
  const es: EntityState<Entity> = { entities: {}, ids: [] };

  const gen = (function* () {
    let i = 0;
    while (true) yield i++;
  })();

  for (const e of arr) {
    if (hasId(e) && !selector) {
      const id = defaultSelector(e);
      es.entities[id] = e;
      es.ids.push(id);
    } else if (selector) {
      const id = selector(e);
      es.entities[id] = e;
      es.ids.push(id);
    } else {
      es.entities[gen.next().value] = e;
    }
  }

  return es;
}
